import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  NOTIFICATION_ENTITIES,
  NOTIFICATION_ENTITY_LABELS,
  NOTIFICATION_FIELDS,
  NOTIFICATION_OPERATOR_LABELS,
  NOTIFICATION_OPERATORS_BY_TYPE,
  NOTIFICATION_RECIPIENT_SOURCES,
  NOTIFICATION_TRIGGERS,
  NOTIFICATION_TRIGGER_LABELS,
  NOTIFICATION_VALUELESS_OPERATORS,
  notificationFieldsFor,
  type CreateNotificationInput,
  type Notification,
  type NotificationEntity,
  type NotificationFieldType,
  type NotificationOperator,
  type NotificationTrigger,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import {
  useCreateNotification,
  useDeleteNotification,
  useRunNotification,
  useUpdateNotification,
} from './api.js';

interface NotificationFormModalProps {
  isOpen: boolean;
  notification: Notification | null;
  onClose: () => void;
}

interface ConditionRow {
  field: string;
  operator: NotificationOperator;
  value: string;
}

interface FormState {
  name: string;
  description: string;
  entity: NotificationEntity;
  trigger: NotificationTrigger;
  conditions: ConditionRow[];
  recipients: string;
  recipientSources: string[];
  subjectTemplate: string;
  bodyTemplate: string;
  enabled: boolean;
}

function firstFieldFor(entity: NotificationEntity): ConditionRow {
  const field = NOTIFICATION_FIELDS[entity]?.[0];
  const type = field?.type ?? 'string';
  const operator = NOTIFICATION_OPERATORS_BY_TYPE[type][0] ?? 'eq';
  return { field: field?.key ?? '', operator, value: '' };
}

function emptyForm(): FormState {
  return {
    name: '',
    description: '',
    entity: 'tickets',
    trigger: 'created',
    conditions: [],
    recipients: '',
    recipientSources: [],
    subjectTemplate: 'New {{number}} — {{title}}',
    bodyTemplate:
      '<p>A ticket was just created.</p>\n<p>#{{number}} — {{title}}</p>\n<p>Status: {{status}} · Priority: {{priority}}</p>\n<p>Customer: {{customer}}</p>',
    enabled: false,
  };
}

function toFormState(notification: Notification | null): FormState {
  if (!notification) return emptyForm();
  return {
    name: notification.name,
    description: notification.description ?? '',
    entity: notification.entity,
    trigger: notification.trigger,
    conditions: notification.conditions.map((c) => ({ ...c })),
    recipients: notification.recipients.join(', '),
    recipientSources: [...notification.recipientSources],
    subjectTemplate: notification.subjectTemplate,
    bodyTemplate: notification.bodyTemplate,
    enabled: notification.enabled,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) result[detail.path] = detail.message;
    return result;
  }
  return {};
}

function parseRecipients(value: string): string[] {
  return value
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const VALUELESS = new Set<NotificationOperator>(NOTIFICATION_VALUELESS_OPERATORS);

export function NotificationFormModal({
  isOpen,
  notification: notificationProp,
  onClose,
}: NotificationFormModalProps): JSX.Element {
  const [notification, setNotification] = useState<Notification | null>(notificationProp);
  const isEdit = notification !== null;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(notification));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);

  const createMutation = useCreateNotification();
  const updateMutation = useUpdateNotification();
  const deleteMutation = useDeleteNotification();
  const runMutation = useRunNotification();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setNotification(notificationProp);
    }
  }, [isOpen, notificationProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(notification));
      setConfirmOpen(false);
      setRunMessage(null);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, notification, resetSaveMode, resetCreate, resetUpdate]);

  const fields = useMemo(() => notificationFieldsFor(form.entity), [form.entity]);
  const recipientSources = NOTIFICATION_RECIPIENT_SOURCES[form.entity] ?? [];
  const fieldType = (key: string): NotificationFieldType =>
    fields.find((f) => f.key === key)?.type ?? 'string';

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save notification.'
        : null;

  const setEntity = (entity: NotificationEntity): void => {
    // Switching entities invalidates conditions + dynamic recipients.
    setForm((f) => ({ ...f, entity, conditions: [], recipientSources: [] }));
  };

  const addCondition = (): void => {
    setForm((f) => ({ ...f, conditions: [...f.conditions, firstFieldFor(f.entity)] }));
  };

  const updateCondition = (index: number, patch: Partial<ConditionRow>): void => {
    setForm((f) => {
      const next = f.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c));
      // When the field changes, reset the operator to the first valid one for its type.
      if (patch.field) {
        const type = fieldType(patch.field);
        const ops = NOTIFICATION_OPERATORS_BY_TYPE[type];
        const current = next[index];
        if (current && !ops.includes(current.operator)) {
          next[index] = { ...current, operator: ops[0] ?? 'eq', value: '' };
        }
      }
      return { ...f, conditions: next };
    });
  };

  const removeCondition = (index: number): void => {
    setForm((f) => ({ ...f, conditions: f.conditions.filter((_, i) => i !== index) }));
  };

  const toggleSource = (key: string): void => {
    setForm((f) => ({
      ...f,
      recipientSources: f.recipientSources.includes(key)
        ? f.recipientSources.filter((s) => s !== key)
        : [...f.recipientSources, key],
    }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const conditions = form.conditions
      .filter((c) => c.field)
      .map((c) => ({
        field: c.field,
        operator: c.operator,
        value: VALUELESS.has(c.operator) ? '' : c.value.trim(),
      }));

    const common = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      trigger: form.trigger,
      conditions,
      recipients: parseRecipients(form.recipients),
      recipientSources: form.recipientSources,
      subjectTemplate: form.subjectTemplate.trim(),
      bodyTemplate: form.bodyTemplate,
      enabled: form.enabled,
    };

    if (isEdit) {
      updateMutation.mutate(
        { id: notification.id, input: common },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      const payload: CreateNotificationInput = { ...common, entity: form.entity };
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setNotification(created);
          }
        },
      });
    }
  };

  const handleRunNow = (): void => {
    if (!notification) return;
    setRunMessage(null);
    runMutation.mutate(notification.id, {
      onSuccess: (result) => setRunMessage(`Matched ${result.matched}, emailed ${result.emailed}.`),
    });
  };

  const handleDelete = (): void => {
    if (!notification) return;
    deleteMutation.mutate(notification.id, { onSuccess: onClose });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="wide"
      title={isEdit ? 'Edit notification' : 'New notification'}
      footer={
        <FormActions
          formId="notification-form"
          saveMode={saveMode}
          isPending={mutation.isPending}
          onCancel={onClose}
          leading={
            isEdit ? (
              <Button
                variant="ghost"
                className="mr-auto text-rose-600"
                onClick={() => setConfirmOpen(true)}
              >
                Delete
              </Button>
            ) : null
          }
          extra={
            isEdit ? (
              <Button variant="secondary" onClick={handleRunNow} isLoading={runMutation.isPending}>
                Run now
              </Button>
            ) : null
          }
        />
      }
    >
      <form id="notification-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={fieldErrors.name}
            required
          />
          <SelectField
            label="Watch"
            value={form.entity}
            disabled={isEdit}
            options={NOTIFICATION_ENTITIES.map((en) => ({
              value: en,
              label: NOTIFICATION_ENTITY_LABELS[en],
            }))}
            onChange={(e) => setEntity(e.target.value as NotificationEntity)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SelectField
            label="Trigger"
            value={form.trigger}
            options={NOTIFICATION_TRIGGERS.map((t) => ({
              value: t,
              label: NOTIFICATION_TRIGGER_LABELS[t],
            }))}
            onChange={(e) =>
              setForm((f) => ({ ...f, trigger: e.target.value as NotificationTrigger }))
            }
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>

        <fieldset className="rounded-lg border border-slate-200 p-4">
          <legend className="px-1 text-sm font-medium text-slate-700">
            Criteria (all must match)
          </legend>
          <p className="mb-2 text-xs text-slate-500">
            Leave empty to notify on every {NOTIFICATION_ENTITY_LABELS[form.entity].toLowerCase()}{' '}
            change.
          </p>
          <div className="space-y-2">
            {form.conditions.map((cond, index) => {
              const type = fieldType(cond.field);
              const ops = NOTIFICATION_OPERATORS_BY_TYPE[type];
              const needsValue = !VALUELESS.has(cond.operator);
              const fieldDef = fields.find((f) => f.key === cond.field);
              return (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <select
                    value={cond.field}
                    onChange={(e) => updateCondition(index, { field: e.target.value })}
                    className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  >
                    {fields.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={cond.operator}
                    onChange={(e) =>
                      updateCondition(index, { operator: e.target.value as NotificationOperator })
                    }
                    className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  >
                    {ops.map((op) => (
                      <option key={op} value={op}>
                        {NOTIFICATION_OPERATOR_LABELS[op]}
                      </option>
                    ))}
                  </select>
                  {needsValue ? (
                    fieldDef?.options ? (
                      <select
                        value={cond.value}
                        onChange={(e) => updateCondition(index, { value: e.target.value })}
                        className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      >
                        <option value="">Select…</option>
                        {fieldDef.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={cond.value}
                        onChange={(e) => updateCondition(index, { value: e.target.value })}
                        placeholder="value"
                        className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      />
                    )
                  ) : null}
                  <button
                    type="button"
                    aria-label="Remove condition"
                    className="text-slate-400 hover:text-rose-600"
                    onClick={() => removeCondition(index)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          <Button
            type="button"
            variant="secondary"
            className="mt-2 h-8 px-2 py-0 text-xs"
            onClick={addCondition}
          >
            + Add condition
          </Button>
        </fieldset>

        <fieldset className="rounded-lg border border-slate-200 p-4">
          <legend className="px-1 text-sm font-medium text-slate-700">Recipients</legend>
          <TextField
            label="Email addresses"
            value={form.recipients}
            placeholder="team@example.com, other@example.com"
            onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
            error={fieldErrors.recipients}
          />
          {recipientSources.length > 0 ? (
            <div className="mt-3">
              <p className="text-xs font-medium text-slate-600">Also email (from each record)</p>
              <div className="mt-1.5 flex flex-wrap gap-3">
                {recipientSources.map((src) => (
                  <CheckboxField
                    key={src.key}
                    label={src.label}
                    checked={form.recipientSources.includes(src.key)}
                    onChange={() => toggleSource(src.key)}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </fieldset>

        <fieldset className="rounded-lg border border-slate-200 p-4">
          <legend className="px-1 text-sm font-medium text-slate-700">Email content</legend>
          <div className="space-y-3">
            <TextField
              label="Subject"
              value={form.subjectTemplate}
              onChange={(e) => setForm((f) => ({ ...f, subjectTemplate: e.target.value }))}
              error={fieldErrors.subjectTemplate}
              required
            />
            <TextAreaField
              label="Body (HTML)"
              rows={6}
              value={form.bodyTemplate}
              onChange={(e) => setForm((f) => ({ ...f, bodyTemplate: e.target.value }))}
              error={fieldErrors.bodyTemplate}
              required
            />
            <div>
              <p className="text-xs font-medium text-slate-600">Available tokens</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {fields.map((f) => (
                  <code
                    key={f.key}
                    className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                    title={f.label}
                  >{`{{${f.key}}}`}</code>
                ))}
              </div>
            </div>
          </div>
        </fieldset>

        <CheckboxField
          label="Enabled (watch for changes and send emails)"
          checked={form.enabled}
          onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
        />

        {runMessage ? <p className="text-sm text-emerald-600">{runMessage}</p> : null}
        {generalError ? <p className="text-sm text-rose-600">{generalError}</p> : null}
      </form>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete notification"
        message={`Delete "${notification?.name ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Modal>
  );
}
